import { animated, useSpring } from '@react-spring/web'
import throttle from 'lodash/throttle'
import React, { useEffect, useState } from 'react'
import { useMatch } from 'react-router-dom'

import { MainRouteName } from '@src/hooks/useAccountRouter'
import { useHasActiveOrders } from '@src/hooks/useHasActiveOrders'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { imageJitURL } from '@src/utils/imageJitURL'
import { useBreakpoint } from '@src/hooks/useBreakpoint'

import { BasketButton } from './BasketButton/BasketButton'
import { ServiceNavigation } from './ServiceNavigation/ServiceNavigation'
import {
  MarketplaceLogo,
  LogoContainer,
  RightIconsContainer,
  ServiceNavigationContainer,
  LogoAndBurgerButtonContainer,
  ActiveOrderDot,
  Container,
  MenuContainer,
  BurgerButton,
  BurgerMenuIcon,
} from './SiteHeader.styles'

import { useCheckoutRouter } from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { AppBanner } from '../AppBanner/AppBanner'
import { useReactiveVar } from '@apollo/client'
import { showAppBannerVar, isSearchFocusedVar } from './utils/reactiveVar'

export const SiteHeader: React.FC = () => {
  const { urlPath, ...marketplace } = useMarketplace()

  const { hasActiveOrders } = useHasActiveOrders()

  const { displayPermanentCheckout } = useCheckoutRouter()
  const showBanner = useReactiveVar(showAppBannerVar)
  const isSearchFocused = useReactiveVar(isSearchFocusedVar)
  const { isMobile } = useBreakpoint()

  const isOutletListRoute = useMatch(`/:deliveryZone?/:merchantCategory`)

  const isSegmentOutletListRoute = useMatch(
    '/segments/:segmentId/:segmentName/:deliveryZone?'
  )

  const isFeaturedOutletListRoute = useMatch('/:deliveryZone?/featured')

  const [scrollData, setScrollData] = useState({
    lastScrollY: 0,
    serviceNavigationIsVisible: true,
  })
  // track the scroll position so we can compare it to the last position to transition the ServiceNavigation
  // when scrolling up and down
  useEffect(() => {
    if (window.onscroll === null) {
      window.onscroll = throttle(
        () => {
          // Don't hide navigation on mobile when search is focused
          if (isSearchFocused && isMobile) {
            return
          }

          const newPosition = window.scrollY
          setScrollData(prevData => ({
            lastScrollY: newPosition,
            serviceNavigationIsVisible:
              newPosition < 200 || newPosition < prevData.lastScrollY,
          }))
        },
        250,
        { leading: true, trailing: true }
      )
    }

    return () => {
      window.onscroll = null
    }
  }, [isSearchFocused, isMobile])
  const hideOnScrollSpring = useSpring({
    translateY:
      scrollData.serviceNavigationIsVisible || (isSearchFocused && isMobile)
        ? '0'
        : '-100%',
    config: {
      tension: 280,
      friction: 60,
      clamp: true, // Prevents overshooting/bouncing
    },
  })
  const AnimatedServiceNavigationContainer = animated(
    ServiceNavigationContainer
  )

  const showServiceNavigation =
    isOutletListRoute || isSegmentOutletListRoute || isFeaturedOutletListRoute

  const content = (
    <>
      <AppBanner />
      <MenuContainer>
        <LogoAndBurgerButtonContainer
          displayPermanentCheckout={displayPermanentCheckout}
        >
          <BurgerButton
            route={{ mainRouteName: MainRouteName.MENU }}
            displayPermanentCheckout={displayPermanentCheckout}
          >
            {hasActiveOrders && <ActiveOrderDot />}
            <BurgerMenuIcon id="profile" dataTestId="siteheader-menu-icon" />
          </BurgerButton>
          <LogoContainer to={{ pathname: '/', search: '' }}>
            <MarketplaceLogo
              role="img"
              aria-label={`${marketplace.name} logo`}
              imageUrl={imageJitURL(marketplace.logoImage, {
                resize: {
                  width: 200,
                  height: 100,
                  fit: 'outside',
                },
              })}
            />
          </LogoContainer>
          {!displayPermanentCheckout && (
            <RightIconsContainer>
              <BasketButton />
            </RightIconsContainer>
          )}
        </LogoAndBurgerButtonContainer>
      </MenuContainer>
      {showServiceNavigation && (
        <AnimatedServiceNavigationContainer
          displayPermanentCheckout={displayPermanentCheckout}
          showAppBanner={showBanner}
          style={hideOnScrollSpring}
        >
          <ServiceNavigation />
        </AnimatedServiceNavigationContainer>
      )}
    </>
  )

  return (
    <Container
      onClick={() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }}
    >
      {content}
    </Container>
  )
}
